import type { FC } from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  TextField,
  Chip,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Extension as ExtensionIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectOnboarding, checkSubdomainAsync, resetSubdomainCheck, updateIntake, saveIntakeAsync } from '../../../store/onboardingSlice';

interface ReviewStepProps {
  onPrev: () => void;
  onSubmit: () => void;
}

const ReviewStep: FC<ReviewStepProps> = ({ onPrev, onSubmit }) => {
  const dispatch = useAppDispatch();
  const { intake, loading, subdomainAvailable, subdomainChecking } = useAppSelector(selectOnboarding);

  const [subdomain, setSubdomain] = useState(intake.preferredSubdomain || '');
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    // Auto-generate subdomain suggestion from company name
    if (!subdomain && intake.legalName) {
      const suggested = intake.legalName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .substring(0, 20);
      setSubdomain(suggested);
    }
  }, [intake.legalName]);

  useEffect(() => {
    // Check subdomain availability when it changes
    const timer = setTimeout(() => {
      if (subdomain.length >= 3) {
        dispatch(checkSubdomainAsync(subdomain));
      } else {
        dispatch(resetSubdomainCheck());
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [subdomain, dispatch]);

  const handleSubdomainChange = (value: string) => {
    // Only allow lowercase letters, numbers, and hyphens
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomain(sanitized);
    dispatch(updateIntake({ preferredSubdomain: sanitized }));
  };

  const handleSubmit = async () => {
    // Save the subdomain preference
    await dispatch(saveIntakeAsync({ ...intake, preferredSubdomain: subdomain }));
    onSubmit();
  };

  const getFeaturesList = () => {
    const features = intake.featuresRequested || {};
    return [
      features.voip && 'VoIP Phone Integration',
      features.email && 'Email Integration',
      features.sms && 'SMS Messaging',
      features.creditBureau && 'Credit Bureau Reporting',
      features.skipTracing && 'Skip Tracing',
    ].filter(Boolean);
  };

  const getPhoneProviderLabel = () => {
    switch (intake.phoneProvider) {
      case 'twilio': return 'Twilio';
      case 'telnyx': return 'Telnyx';
      case 'custom_sip': return 'Custom SIP';
      case 'manual': return 'Manual Configuration';
      default: return 'Not configured';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Review Your Setup
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Review your configuration before submitting. You can change these settings later.
      </Typography>

      <Grid container spacing={3}>
        {/* Business Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BusinessIcon color="primary" />
                <Typography variant="h6">Business Information</Typography>
              </Box>
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Legal Name"
                    secondary={intake.legalName || 'Not provided'}
                  />
                </ListItem>
                {intake.dbaName && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="DBA"
                      secondary={intake.dbaName}
                    />
                  </ListItem>
                )}
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Business Type"
                    secondary={intake.businessType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not provided'}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Address"
                    secondary={`${intake.addressLine1 || ''}, ${intake.city || ''}, ${intake.state || ''} ${intake.zipCode || ''}`}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Compliance */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SecurityIcon color="primary" />
                <Typography variant="h6">Compliance</Typography>
              </Box>
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Operating States"
                    secondary={
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {(intake.operatingStates || []).length > 0 ? (
                          intake.operatingStates?.slice(0, 10).map((state) => (
                            <Chip key={state} label={state} size="small" />
                          ))
                        ) : (
                          'None selected'
                        )}
                        {(intake.operatingStates?.length || 0) > 10 && (
                          <Chip label={`+${(intake.operatingStates?.length || 0) - 10} more`} size="small" variant="outlined" />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Bonded"
                    secondary={intake.bondingInfo?.isBonded ? 'Yes' : 'No'}
                  />
                </ListItem>
                {intake.complianceOfficerName && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Compliance Officer"
                      secondary={intake.complianceOfficerName}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Integrations */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ExtensionIcon color="primary" />
                <Typography variant="h6">Integrations</Typography>
              </Box>
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Enabled Features"
                    secondary={
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {getFeaturesList().map((feature, idx) => (
                          <Chip key={idx} label={feature} size="small" color="primary" variant="outlined" />
                        ))}
                        {getFeaturesList().length === 0 && 'None selected'}
                      </Box>
                    }
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Estimated Users"
                    secondary={intake.estimatedUsers || 1}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Data Migration"
                    secondary={intake.dataMigrationNeeded ? `Yes - from ${intake.currentSoftware || 'another system'}` : 'No'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Phone Configuration */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PhoneIcon color="primary" />
                <Typography variant="h6">Phone Configuration</Typography>
              </Box>
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Provider"
                    secondary={getPhoneProviderLabel()}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Subdomain Selection */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LanguageIcon color="primary" />
            <Typography variant="h6">Choose Your Subdomain</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will be your Debtornator URL. Choose carefully - this cannot be changed later.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              value={subdomain}
              onChange={(e) => handleSubdomainChange(e.target.value)}
              placeholder="yourcompany"
              inputProps={{ maxLength: 30 }}
              sx={{ width: 200 }}
            />
            <Typography color="text.secondary">.debtornator.com</Typography>
            {subdomainChecking && <CircularProgress size={20} />}
            {!subdomainChecking && subdomainAvailable === true && (
              <Chip icon={<CheckIcon />} label="Available" color="success" size="small" />
            )}
            {!subdomainChecking && subdomainAvailable === false && (
              <Chip label="Not available" color="error" size="small" />
            )}
          </Box>
          {subdomain.length > 0 && subdomain.length < 3 && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              Subdomain must be at least 3 characters
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Terms */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I agree to the{' '}
                <a href="/terms" target="_blank" style={{ color: 'inherit' }}>Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" style={{ color: 'inherit' }}>Privacy Policy</a>
              </Typography>
            }
          />
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={onPrev}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={
            loading ||
            !termsAccepted ||
            !subdomainAvailable ||
            subdomain.length < 3
          }
          endIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
        >
          {loading ? 'Submitting...' : 'Submit & Continue to Payment'}
        </Button>
      </Box>
    </Box>
  );
};

export default ReviewStep;
